import { useQueryClient } from "@tanstack/react-query";
import { Dropdown } from "@trafficsharkdsp/ui";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { getNotificationOptions } from "../lib/getNotificationOptions";

import cls from "./NotificationDropdown.module.css";

import { useGetNotifications } from "@/entities/notification/api/useGetNotifications";
import { useReadNotifications } from "@/entities/notification/api/useReadNotifications";
import { useSessionStore } from "@/entities/session/models/store/useSessionStore";
import NotificationIcon from "@/shared/assets/icons/notificationIcon.svg";
import { useCentrifugeSubscription } from "@/shared/lib/useCentrifugeConnect";

interface SocketNotifications {
    description?: string;
    uuid: string;
    is_read: boolean;
    owner: string;
}

export const NotificationDropdown = () => {
    const { t } = useTranslation();
    const { mutate } = useReadNotifications();
    const client = useQueryClient();
    const { data: notificationData } = useGetNotifications({ type: "TransactionNotificationType" });
    const [notifications, setNotifications] = useState<SocketNotifications[]>([]);
    const allNotifications = useMemo(
        () => [...notifications, ...(notificationData || [])],
        [notificationData, notifications],
    );
    const { userInfo } = useSessionStore();

    const emptyItem = [
        {
            key: "empty",
            label: (
                <div className={cls.empty}>
                    {t("notifications.empty", { defaultValue: "No notifications" })}
                </div>
            ),
        },
    ];

    const onOpen = useCallback(() => {
        const unReadNotifications = allNotifications?.filter(
            (notification) => notification.is_read === false,
        );
        if (unReadNotifications && unReadNotifications.length > 0)
            return mutate(
                {
                    uuids: unReadNotifications
                        ?.filter((not) => !!not.uuid)
                        ?.map((notification) => notification.uuid || ""),
                },
                {
                    onSuccess: () => {
                        client.invalidateQueries({ queryKey: ["getApiInternalNotifications"] });
                        setNotifications([]);
                    },
                },
            );
    }, [allNotifications]);

    const notificationItems = getNotificationOptions(allNotifications || []);
    const isEmpty = notificationItems.length === 0;
    const hasUnreadNotifications = allNotifications?.some((notification) => !notification.is_read);
    const notificationChannel = userInfo.user_id ? `${userInfo.user_id}/notifications` : "";

    useCentrifugeSubscription<SocketNotifications>(notificationChannel, (newValue) => {
        if (newValue) {
            return setNotifications((prev) => [{ ...newValue, is_read: false }, ...prev]);
        }
    });

    return (
        <Dropdown
            listClassName={cls.dropdown}
            items={isEmpty ? emptyItem : notificationItems}
            width="32rem"
            onToggle={(value) => value && onOpen()}
            gap="2.35rem"
            renderDropdown={(_, items) => {
                return (
                    <div className={cls["list-wrapper"]}>
                        <div className={cls.list}>
                            {items?.map((item) => {
                                return <span key={item.key}>{item.label}</span>;
                            })}
                        </div>
                    </div>
                );
            }}
            position="rightBottom"
            aria-label="Notifications"
            data-qa="header-notifications-button"
        >
            {hasUnreadNotifications && <div className={cls["unread-indicator"]} />}
            <NotificationIcon className={cls["notification-icon"]} />
        </Dropdown>
    );
};
